import React, { ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaClipboardList,
  FaThLarge,
  FaBars,
  FaCog,
  FaHandshake,
  FaBoxes,
  FaUserTie,
  FaSignOutAlt,
  FaCreditCard,
  FaStar,
  FaToolbox,
  FaHistory,
  FaUserCheck,
  FaChartLine,
  FaFolderOpen,
  FaReceipt,
} from "react-icons/fa";
// import Logo from "../assets/logo.png"
import Logo from "../assets/logo.png";
import { initAllStores } from "../utils/cache";
import ResetPasswordModal from "../Modal/staff/ResetPasswordModal";
import type User from "../models/User";
import { errorMessage, sendRequest } from "../utils/api";
import { useModal } from "../context/ConfirmModalContext";
import { useToast } from "../context/ToastContext";
import ChatBubble from "./ChatBubble";
import { getUser, } from "../utils/user-helper";
import ProfileAvatar from "./ProfileAvatar";

interface DashboardLayoutProps {
  children: ReactNode;
}

const menuItems = [
  { name: "Dashboard", path: "/dashboard" },
  { name: "Attendances", path: "/attendances" },
  { name: "Logs", path: "/logs" },
  { name: "Staff", path: "/staffs" },
  { name: "Expenses", path: "/expenses" },
  // { name: "Partners", path: "/partners" },
  { name: "Project Reports", path: "/project-reports" },
  { name: "Documents", path: "/documents" },
  // { name: "Help & Support", path: "/help" },
  { name: "Settings", path: "/settings" },
  { name: "Logout", path: "/logout" },
];

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile toggle
  const [user, setUser] = useState<{
    name: string; email: string,
    photoUrl?: string

  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // const [allowChat, setAllowChat] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [allowMenu, setAllowMenu] = useState(menuItems)

  const navigate = useNavigate();

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();


  //   useEffect(() => {
  //   if (terminalId) {
  //     localStorage.setItem("terminal", terminalId);
  //   }
  // }, [terminalId]);

  // Load user from cache
  useEffect(() => {
    (async () => {

      const data = await getUser();

      if (!data) {
        return openModal(
          "Session Expired ⚠️",
          "We couldn’t find your user information. Please log in again to continue.",
          () => {
            closeModal();
            window.location.href = "/login";
          },
          false,
          "Login Again"
        );
      }

      const { user, } = data;

      const chatBubbleSetting = user?.settings?.find(st => st.name);
      const allowChatByUser = !!(chatBubbleSetting && chatBubbleSetting.value === "show");
      // const allowChatByHotel = hotel?.preference?.chat ?? true;
      // setAllowChat(allowChatByHotel && allowChatByUser);


      let name: string = user.firstName + " " + user.lastName;
      name = name.length > 20 ? name.slice(0, 15) + "..." : name;
      setUser({ ...user, name });

      // Compute accessible menus
      const menuViews = (user?.permissions ?? [])
        .filter((perm: any) => perm.type === "view" && perm.value === true)
        .map((perm: any) => perm.name.toLowerCase());

      // Extend logic for grouped permissions
      const expandedViews = new Set(menuViews);

      // Filter menus based on expanded permissions
      const filteredMenus = menuItems.filter((item) =>
        expandedViews.has(item.name.toLowerCase())
      );

      setAllowMenu(filteredMenus);
    })();
  }, []);

  useEffect(() => {
    const forceResetPassword = async () => {
      const cache = await initAllStores();
      const user: User | null = await cache.getBy("data", { id: "user" });
      if (!user) await handleLogout();

      if (user?.forceReset) setIsModalOpen(true);
    };
    forceResetPassword();
  }, []);

  const handleLogout = async () => {
    try {
      await sendRequest("DELETE", "/auth/logout");
    } catch (err: any) {
      console.log(err);
    }
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  const handleReset = async (newPassword: string) => {
    const cache = await initAllStores();
    try {
      await sendRequest("PUT", "/auth/reset-password/confirm", { newPassword });
      await cache.updateManyBy("data", ["user"], { forceReset: false });
      setIsModalOpen(false);
      showToast("Password Reset Successful", { type: "success" });

    } catch (error: any) {
      const err = errorMessage(error.status);
      if (err) openModal(err.title, err.msg, closeModal, false, "Close");
      console.log(error);
    }
  };


  return (

    // <div className="flex h-screen bg-gray-50">
    //   <div
    //     className={`bg-gray-200 ${isCollapsed ? "w-20" : "w-64"
    //       } flex flex-col justify-between fixed md:relative h-full z-20 transform transition-all duration-300 ease-in-out
    // ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 p-2 md:p-4`}
    //   >

    <div className="flex h-screen bg-gray-50">
      <div
        className={`bg-gray-200 ${isCollapsed ? "w-20 md:w-20" : "w-64 md:w-64"
          } shrink-0 flex flex-col justify-between fixed md:relative h-full z-20 transform transition-all duration-300 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 p-2 md:p-4`}
      >
        {/* Top Section */}
        <div className="relative mb-4 flex items-center justify-between">
          {/* Logo — top-left */}
          <div className="flex items-center pl-1 pt-2">
            <img
              src={Logo}
              alt="Logo"
              className={`transition-all duration-300 ease-in-out rounded-md ${isCollapsed ? "w-8 h-8" : "w-10 h-10"
                }`}
            />
            {!isCollapsed && (
              <span className="ml-2 font-semibold text-gray-800 text-base">
                Chrome
              </span>
            )}
          </div>

          {/* Collapse Toggle - top right */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute right-0 top-2 p-2 rounded-md hover:bg-gray-300 transition"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <span className="text-gray-700 text-lg">{FaChevronRight({})}</span>
            ) : (
              <span className="text-gray-700 text-lg">{FaChevronLeft({})}</span>
            )}
          </button>
        </div>

        {/* Scrollable Menu */}
        <div className="flex-1 overflow-y-auto p-1 md:p-2 space-y-2">
          {allowMenu.map((item) => {
            // const label = !isCollapsed ? item.name : "";

            // Assign icons for each menu
            const getIcon = (name: string) => {
              switch (name.toLowerCase()) {
                case "dashboard":
                  return FaThLarge({});

                case "logs":
                  return FaHistory({});

                case "inventory":
                  return FaBoxes({});

                case "staff":
                  return FaUserTie({});

                case "roster":
                  return FaClipboardList({});

                case "settings":
                  return FaCog({});

                case "maintenances":
                  return FaToolbox({});

                case "expenses":
                  return FaCreditCard({});

                case "expenses":
                  return FaReceipt({});

                case "logout":
                  return FaSignOutAlt({});

                case "reviews":
                  return FaStar({});

                case "attendances":
                  return FaUserCheck({});

                case "partners":
                  return FaHandshake({});

                case "project reports":
                  return FaChartLine({});

                case "documents":
                  return FaFolderOpen({});

                default:
                  return FaClipboardList({});
              }
            };

            if (item.name === "Logout") {
              return (
                <button
                  key={item.name}
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between px-2 py-2 rounded bg-gray-200 hover:bg-gray-300"
                >
                  <div className="flex items-center space-x-2">
                    {getIcon(item.name)}
                    {!isCollapsed && <span>{item.name}</span>}
                  </div>
                </button>
              );
            }

            // if (item.name === "Attendances") {
            //   // navigate("/location-required")
            // }

            return (
              <NavLink
                key={item.name}
                to={item.path}
                className={({ isActive }) =>
                  `w-full flex items-center justify-between px-2 py-2 rounded relative ${isActive
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                  }`
                }
              >
                <div className="flex items-center space-x-2 capitalize">
                  {getIcon(item.name)}
                  {!isCollapsed && <span>{
                    item.name}</span>
                  }
                </div>
              </NavLink>
            );
          })}
        </div>

        {/* User Profile */}
        {!isCollapsed && (
          <div
            onClick={() => navigate("/profile")}
            className="p-2 md:p-4 flex items-center space-x-2 md:space-x-3 bg-gray-100 rounded-lg shadow-sm cursor-pointer">
            <div className="w-10 h-10 rounded-full bg-gray-400 flex items-center justify-center text-white font-semibold">
              <ProfileAvatar
                photoUrl={user?.photoUrl}
                firstName={user?.name}
              />
            </div>

            <div>
              <div
                className="font-bold text-black text-sm md:text-base"
                title={user?.name}
              >
                {(user?.name ?? "").length > 15
                  ? `${(user?.name ?? "").slice(0, 15)}...`
                  : user?.name ?? ""}
              </div>
              <div className="text-xs md:text-sm text-black" title={user?.email}>
                {(user?.email ?? "").length > 15
                  ? `${(user?.email ?? "").slice(0, 15)}...`
                  : user?.email ?? ""}
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-10 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        {/* <div className="flex items-center justify-between bg-white p-2 md:hidden shadow-md">
          <h1 className="text-lg md:text-xl font-bold">Dashboard</h1>
          <button onClick={() => setSidebarOpen(!sidebarOpen)}>
            {FaBars({ size: 22 })}
          </button>
        </div> */}
        <div className="relative flex items-center bg-white p-2 md:hidden shadow-md">
          {/* Hamburger */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="z-10 ml-1"
          >
            {FaBars({ size: 22 })}
          </button>

          {/* Centered title */}
          <h1 className="absolute left-1/2 -translate-x-1/2 text-lg font-bold">
            Chrome Architecture
          </h1>
        </div>

        {/* Children */}
        <div className="flex-1 overflow-auto md:p-1">
          <ResetPasswordModal
            isOpen={isModalOpen}
            showCloseBtn={false}
            onClose={() => setIsModalOpen(false)}
            onReset={handleReset}
          />

          {/* Floating Chat Bubble */}
          {/* {allowChat && < ChatBubble />} */}

          {children}
        </div>
        {/* <div className="flex-1 overflow-auto p-1 md:p-6">{children}</div> */}
      </div>
    </div>
  );

};

export default DashboardLayout;